import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, User, Heart, Users, Baby, Sparkles, HeartHandshake, ShieldCheck, ClipboardList, Route, Video, Building2 } from "lucide-react";
import { Seo, faqJsonLd } from "@/components/Seo";
import { PageHead, Section, Reveal, CtaBand, QaBlock } from "@/components/Shared";
import { UI, PATHS } from "@/i18n/ui";
import { SERVICES, serviceCommon, findService } from "@/data/services";
import { PRICES } from "@/data/prices";
import { SITE } from "@/data/site";

const ICONS = { User, Heart, Users, Baby, Sparkles, HeartHandshake, ShieldCheck, ClipboardList, Route, Video, Building2 };

export function ServicesPage({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const meta = {
    id: { title: "Layanan Psikologi di Denpasar, Bali | Bali Empower", desc: "Direktori lengkap layanan psikologi Bali Empower di Denpasar: konseling individu, pasangan, keluarga, anak & remaja, pranikah, pernikahan, trauma, asesmen psikologi, dan program kesehatan mental." },
    en: { title: "Psychology Services in Denpasar, Bali | Bali Empower", desc: "Complete directory of Bali Empower's psychology services in Denpasar: individual, couples, family, child & adolescent, premarital, marriage and trauma counseling, psychological assessment and mental health programs." },
  }[lang];
  return (
    <div data-testid="services-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.services} title={t.nav.services}
        sub={lang === "id"
          ? "Setiap layanan dirancang untuk kebutuhan yang berbeda. Pilih layanan yang paling sesuai, atau hubungi kami untuk membantu menentukan langkah awal yang tepat."
          : "Each service is designed for a different need. Choose the one that fits best, or contact us to help determine the right first step."} />
      <Section className="pt-0">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.key} delay={i * 0.04}>
                <Link to={p.service(s.slug[lang])} data-testid={`services-index-card-${s.key}`}
                  className="be-card block h-full rounded-2xl border border-[#E5E1D8] bg-white p-8">
                  <Icon size={24} className="text-[#4A6B53]" strokeWidth={1.6} />
                  <h2 className="mt-4 text-lg font-display text-[#1F2937]">{s.name[lang]}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">{s.short[lang]}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                    {t.cta.learnMore} <ArrowRight size={14} />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

const buildServiceQa = (s, lang, m) => [
  [m.whatIs, s.what[lang]],
  [m.whoFor, s.who[lang]],
  [m.firstSession, s.firstSession[lang]],
  [m.duration, serviceCommon.duration[lang]],
  [m.sessions, serviceCommon.sessions[lang]],
  [m.provider, serviceCommon.provider[lang]],
  [m.format, serviceCommon.format[lang]],
  [m.cost, (PRICES[s.key] || serviceCommon.cost)[lang]],
  [m.howBook, serviceCommon.booking[lang]],
];

const ServiceSidebar = ({ lang, t, p }) => (
  <aside>
    <div className="rounded-2xl border border-[#E5E1D8] bg-white p-8 sticky top-24">
      <h2 className="font-display text-xl text-[#1F2937]">{t.cta.book}</h2>
      <p className="mt-2 text-sm text-[#4B5563]">
        {lang === "id" ? "Mulai dengan konsultasi awal — prosesnya kurang dari 2 menit." : "Start with an initial consultation — it takes under 2 minutes."}
      </p>
      <Link to={p.booking} data-testid="service-sidebar-book"
        className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white font-medium px-6 py-3.5 hover:bg-[#3A5542] transition-colors">
        {t.cta.book} <ArrowRight size={15} />
      </Link>
      <Link to={p.services} data-testid="service-sidebar-all"
        className="mt-3 flex items-center justify-center rounded-full border border-[#E5E1D8] text-[#1F2937] text-sm font-medium px-6 py-3 hover:border-[#4A6B53] transition-colors">
        {t.misc.allServices}
      </Link>
    </div>
  </aside>
);

export function ServiceDetail({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const { slug } = useParams();
  const s = findService(lang, slug);
  if (!s) return <Navigate to={p.services} replace />;
  const m = t.misc;
  const qa = buildServiceQa(s, lang, m);
  const meta = {
    id: { title: `${s.name.id} di Denpasar, Bali | Bali Empower`, desc: `${s.short.id} Layanan ${s.name.id.toLowerCase()} profesional oleh Bali Empower di Renon, Denpasar Selatan, Bali.` },
    en: { title: `${s.name.en} in Denpasar, Bali | Bali Empower`, desc: `${s.short.en} Professional ${s.name.en.toLowerCase()} by Bali Empower in Renon, Denpasar Selatan, Bali.` },
  }[lang];
  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE.url}/psychology-services/#${s.slug.en}`,
    name: s.name[lang],
    description: s.what[lang],
    provider: { "@id": "https://baliempower.com/#organization" },
    areaServed: "Denpasar, Bali, Indonesia",
  }, faqJsonLd(qa)];

  return (
    <div data-testid={`service-detail-${s.key}`}>
      <Seo title={meta.title} description={meta.desc} lang={lang} jsonLd={jsonLd} />
      <PageHead eyebrow={t.nav.services} title={s.name[lang]} sub={s.what[lang]} />
      <Section className="pt-0">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <QaBlock q={m.whoFor}><p>{s.who[lang]}</p></QaBlock>
            <QaBlock q={m.whenConsider}>
              <ul className="list-disc pl-5 space-y-1.5">
                {s.when[lang].map((w) => <li key={w}>{w}</li>)}
              </ul>
            </QaBlock>
            {qa.slice(2).map(([q, a]) => <QaBlock key={q} q={q}><p>{a}</p></QaBlock>)}
          </div>
          <ServiceSidebar lang={lang} t={t} p={p} />
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}
